import { i18n, Resource } from 'i18next';
import { useWorkbenchLocale } from '@tanium/react-locale-context';
import { getCalculatedLocale } from '../getCalculatedLocale';
import { StrictTFunction } from '../types/StrictTFunction';
import { TFunction } from '../types/TFunction';
import { generateUseInvalidUsageWarning } from './generateUseInvalidUsageWarning';
import { getLocalTFunction } from './getLocalTFunction';
import { create as createTFunctionStore } from './tFunctionStore';
import { useRemoteTFunction } from './useRemoteTFunction';

export type StrictUseTranslation<TPackageName extends string> = () => {
  t: StrictTFunction<TPackageName>;
  ready: boolean;
};

export type UseTranslation = () => {
  t: TFunction;
  ready: boolean;
};

export const generateUseTranslation = (
  i18nInstance: i18n,
  packageName: string,
  resources: Resource,
) => {
  const remoteTFunctionStore = createTFunctionStore();
  const localTFunctions: Map<string, TFunction> = new Map();

  const useInvalidUsageWarning = generateUseInvalidUsageWarning(packageName);

  const useTranslation: UseTranslation = () => {
    useInvalidUsageWarning();

    const workbenchLocale = useWorkbenchLocale();
    const calculatedLocale = getCalculatedLocale(resources, workbenchLocale);

    const remoteTFunction = useRemoteTFunction(
      calculatedLocale,
      packageName,
      i18nInstance,
      remoteTFunctionStore,
    );

    if (remoteTFunction) {
      return { t: remoteTFunction, ready: true };
    }

    const localTFunction = getLocalTFunction(
      calculatedLocale,
      packageName,
      i18nInstance,
      localTFunctions,
    );

    return { t: localTFunction, ready: true };
  };

  return useTranslation;
};
